import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { QUERY_KEYS } from '../../constants';
import type { MarketplaceConfig } from '../marketplace';
import {
  prefetchFeaturedSportLandingEvents,
  prefetchLiveSports,
  prefetchOpenMyBetsFirstPage,
  prefetchPreMatchSports,
} from './sports';

const runWhenIdle = (cb: () => void) => {
  if (typeof window === 'undefined') {
    return;
  }

  const idle = (window as Window & typeof globalThis).requestIdleCallback;

  if (typeof idle === 'function') {
    idle(cb);
    return;
  }

  window.setTimeout(cb, 0);
};

export const usePrefetchManager = () => {
  const queryClient = useQueryClient();
  const isFeedRunning = useCallback(
    () => queryClient.getQueryData<MarketplaceConfig>([QUERY_KEYS.marketplaceConfig])?.feedRunning === true,
    [queryClient]
  );

  const prefetchInitialData = useCallback(async () => {
    if (!isFeedRunning()) return;

    await prefetchLiveSports(queryClient);

    runWhenIdle(() => {
      if (!isFeedRunning()) return;

      void prefetchPreMatchSports(queryClient).then(() => {
        if (isFeedRunning()) return prefetchFeaturedSportLandingEvents(queryClient);
      });
    });
  }, [queryClient, isFeedRunning]);

  const prefetchSportsLanding = useCallback(() => {
    if (!isFeedRunning()) return;

    void prefetchLiveSports(queryClient);
    void prefetchPreMatchSports(queryClient);
  }, [queryClient, isFeedRunning]);

  const prefetchInPlayLanding = useCallback(() => {
    if (!isFeedRunning()) return;

    void prefetchLiveSports(queryClient);
  }, [queryClient, isFeedRunning]);

  const prefetchMyBets = useCallback(() => {
    void prefetchOpenMyBetsFirstPage(queryClient);
  }, [queryClient]);

  return {
    prefetchInitialData,
    prefetchSportsLanding,
    prefetchInPlayLanding,
    prefetchMyBets,
  };
};
